import LazyLoadBackgroundImg from 'Components/Helpers/LazyLoadBackgroundImg';
import { Button, Flex, Icon, Text, Title } from '@upsales/components';
import { DefaultButton } from '@upsales/components/Buttons/Buttons';
import { useTranslation } from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import FileService from '../../../babel/resources/File';
import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import './File.scss';

type Props = {
	id: number;
	name: string;
	fileType: string;
	deleteFile: (id: number) => void;
	userRemovable: boolean;
};

const File = ({ id, name, fileType, deleteFile, userRemovable }: Props) => {
	const { t } = useTranslation();
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
	const classes = new BemClass('File');
	const onlyFileType = fileType.slice(1);
	const onlyFileName = name.split('.')[0];
	const lang = {
		irreversibleAction: t('default.thisActionIsIrreversable'),
		deleteFile: t('file.removeFile'),
		delete: t('default.delete')
	};

	const downloadFile = (fileId: number) => {
		if (fileId) {
			window.Tools.File.download(fileId);
		}
	};
	const thubnailUrl = FileService.getInlineThumbnailUrl(id);

	const [{ isDragging }, drag] = useDrag(
		() => ({
			type: 'ALL',
			item: { id, isFile: true },
			canDrag: !isDeleteConfirmOpen,
			collect: monitor => ({ isDragging: !!monitor.isDragging() })
		}),
		[isDeleteConfirmOpen]
	);

	const renderImg = () => {
		switch (onlyFileType) {
			case 'png':
			case 'jpg':
			case 'jpeg':
			case 'gif':
				return true;
			default:
				return false;
		}
	};

	const DeleteButton = () => {
		return (
			<Button
				type="link"
				className={classes.elem('delete-button').b()}
				onClick={e => {
					e.stopPropagation();
					setIsDeleteConfirmOpen(prev => !prev);
				}}
			>
				<Icon name="trash" color="white" />
			</Button>
		);
	};

	const DownloadButton = () => {
		return (
			<DefaultButton
				className={classes.elem('download-button').b()}
				icon="arrow-down"
				onClick={() => downloadFile(id)}
			>
				{t('document.downloadFile')}
			</DefaultButton>
		);
	};

	const CloseDeleteConfirmButton = () => {
		return (
			<Button
				color="grey"
				type="link"
				className={classes.elem('delete-confirm-close').b()}
				onClick={e => {
					e.stopPropagation();
					setIsDeleteConfirmOpen(false);
				}}
			>
				<Icon name="times" />
			</Button>
		);
	};

	const ConfirmDeleteButton = () => {
		return (
			<Button
				block
				color="red"
				onClick={e => {
					e.stopPropagation();
					deleteFile(id);
				}}
			>
				{lang.delete}
			</Button>
		);
	};

	return (
		<div ref={drag} className={classes.mod({ isDragging, isDeleteConfirmOpen }).b()}>
			<div className={classes.elem('image-wrapper').b()}>
				<LazyLoadBackgroundImg background={thubnailUrl} asImg={renderImg()} />
				{userRemovable && !isDeleteConfirmOpen ? <DeleteButton /> : null}
				<Flex
					direction="column"
					justifyContent="center"
					alignItems="center"
					gap={8}
					className={classes.elem('overlay-buttons').b()}
				>
					<DownloadButton />
				</Flex>
				<div className={classes.elem('delete-confirm').b()}>
					<div className={classes.elem('delete-confirm-inner').b()}>
						<CloseDeleteConfirmButton />
						<Title>{lang.deleteFile + '?'}</Title>
						<Text color="grey-10">{lang.irreversibleAction}</Text>
						<ConfirmDeleteButton />
					</div>
				</div>
			</div>
			<div className={classes.elem('file-name-wrapper').b()}>
				<Text className={classes.elem('file-name').b()}>{onlyFileName}</Text>
				<Text size="sm" color="grey-11" className={classes.elem('file-type').b()}>
					{onlyFileType}
				</Text>
			</div>
		</div>
	);
};

export default File;
